/**
 * The Context tab container.
 *
 * Owns the SDK, the mutations, and the dialog state; the list, editor, and
 * profile panel below it are presentational. That split keeps every visual
 * state drivable from the functional tier without a fake backend.
 *
 * HEALTH comes from the account-scoped `GET /v1/context/sources/health` plus
 * live `context_health` session events while a chat is open. Never-observed
 * sources are ABSENT from the health response — the client derives
 * `not_yet_observed` from that absence. The platform-reserved Built-in
 * row carries no health affordance.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { ContextSource, ContextSourceHealth, UserProfilePatch } from '@localagent/sdk';
import { chatSessionAtom } from '@localagent/sdk/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { Button } from '@localagent/ui/components/button';
import { ContextSourceList, type SourceHealthMap } from './ContextSourceList';
import { ContextSourceEditor, type ContextSourceEditorValues } from './ContextSourceEditor';
import { ProfilePanel } from './ProfilePanel';
import {
  useContextSourceHealth,
  useContextSources,
  useCreateContextSource,
  useDeleteContextSource,
  useMoveContextSource,
  useUpdateContextSource,
  useUpdateUserProfile,
  useUserProfile,
} from '../../hooks/useContextSources';

const VALID_HEALTH = new Set<ContextSourceHealth>(['healthy', 'omitted', 'error']);

export function ContextTab() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ContextSource | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleting, setDeleting] = useState<ContextSource | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  /** Live overrides from an open chat's context_health events, keyed by source_id. */
  const [liveHealth, setLiveHealth] = useState<Record<string, ContextSourceHealth>>({});

  const sourcesQuery = useContextSources();
  const healthQuery = useContextSourceHealth();
  const profileQuery = useUserProfile();
  const chatSession = useAtomValue(chatSessionAtom);

  const createMutation = useCreateContextSource();
  const updateMutation = useUpdateContextSource();
  const deleteMutation = useDeleteContextSource();
  const moveMutation = useMoveContextSource();
  const profileMutation = useUpdateUserProfile();

  const sources = useMemo(
    () => sourcesQuery.data?.sources ?? [],
    [sourcesQuery.data],
  );

  useEffect(() => {
    if (!chatSession) return;
    return chatSession.onContextHealth((payload) => {
      setLiveHealth((prev) => {
        const next = { ...prev };
        for (const s of payload.sources) {
          if (VALID_HEALTH.has(s.health as ContextSourceHealth)) {
            next[s.source_id] = s.health as ContextSourceHealth;
          }
        }
        return next;
      });
    });
  }, [chatSession]);

  /**
   * Derive the display health map: observed health from the account read +
   * live overrides; sources present in the list but absent from both →
   * `not_yet_observed`. Built-in platform-reserved is skipped.
   */
  const healthMap = useMemo((): SourceHealthMap | undefined => {
    if (!healthQuery.isSuccess && Object.keys(liveHealth).length === 0) {
      return undefined;
    }
    const observed = new Map<string, ContextSourceHealth>();
    for (const entry of healthQuery.data?.sources ?? []) {
      if (VALID_HEALTH.has(entry.health)) {
        observed.set(entry.source_id, entry.health);
      }
    }
    for (const [id, h] of Object.entries(liveHealth)) {
      observed.set(id, h);
    }
    const map: SourceHealthMap = {};
    for (const s of sources) {
      if (s.id === 'platform-reserved') continue;
      map[s.id] = observed.get(s.id) ?? 'not_yet_observed';
    }
    return map;
  }, [healthQuery.isSuccess, healthQuery.data, liveHealth, sources]);

  /** Surface a failed mutation on the row rather than swallowing it. */
  const run = useCallback(async (op: Promise<unknown>) => {
    setRowError(null);
    try {
      await op;
    } catch (err) {
      setRowError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleSave = async (values: ContextSourceEditorValues) => {
    if (editing) {
      // scope/type/data_source/placement are not patchable server-side —
      // changing one is a delete-and-recreate — so only the mutable fields go
      // up. Sending the rest would earn an explained 422, not a silent drop.
      await updateMutation.mutateAsync({
        sourceId: editing.id,
        req: {
          name: values.name,
          ...(values.body !== undefined ? { body: values.body } : {}),
          ...(values.reference_path !== undefined
            ? { reference_path: values.reference_path }
            : {}),
          ...(values.target_persona !== undefined
            ? { target_persona: values.target_persona }
            : {}),
        },
      });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  const handleProfileSave = async (patch: UserProfilePatch) => {
    await profileMutation.mutateAsync(patch);
  };

  const busy =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    moveMutation.isPending;

  return (
    <div className="flex flex-col gap-8 py-2" data-testid="context-tab">
      <ContextSourceList
        sources={sources}
        health={healthMap}
        isLoading={sourcesQuery.isLoading}
        isError={sourcesQuery.isError}
        error={sourcesQuery.error}
        searchValue={search}
        onSearchChange={setSearch}
        onCreate={() => {
          setEditing(null);
          setEditorOpen(true);
        }}
        onEdit={(source) => {
          setEditing(source);
          setEditorOpen(true);
        }}
        onDelete={setDeleting}
        onToggle={(source, enabled) =>
          void run(
            updateMutation.mutateAsync({
              sourceId: source.id,
              req: { enabled },
            }),
          )
        }
        onMove={(source, position) =>
          void run(moveMutation.mutateAsync({ sourceId: source.id, position }))
        }
        rowError={rowError}
        busy={busy}
      />

      <ProfilePanel
        profile={profileQuery.data?.profile}
        effective={profileQuery.data?.effective}
        isLoading={profileQuery.isLoading}
        isError={profileQuery.isError}
        loadError={profileQuery.error}
        onSave={handleProfileSave}
      />

      <ContextSourceEditor
        source={editing}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />

      <Dialog
        open={deleting !== null}
        onOpenChange={(isOpen) => { if (!isOpen) setDeleting(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this source?</DialogTitle>
            <DialogDescription>
              {deleting
                ? `"${deleting.name}" will stop being given to your agents. This cannot be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="context-source-delete-confirm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const target = deleting;
                if (!target) return;
                setDeleting(null);
                void run(deleteMutation.mutateAsync(target.id));
              }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
